import { useMemo } from 'react';

import type { AiCoach_AdditionalContextInput as AiCoachAdditionalContextInput } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

type SurfaceInfo = {
  surface_type: string;
  topical_anchor?: string;
};

const slugToDisplayName = (slug: string): string => {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getSurfaceInfoFromPath = (pathname: string): SurfaceInfo | null => {
  // Career Academy & RDP: /career-academy, /career-academy/roles/:roleSlug
  if (pathname.startsWith('/career-academy')) {
    const roleMatch = pathname.match(/^\/career-academy(?:\/roles\/([^/]+))?/);
    const roleSlug = roleMatch?.[1];
    return { surface_type: 'career_academy', ...(roleSlug ? { topical_anchor: slugToDisplayName(roleSlug) } : {}) };
  }

  // LOHP: /, /frontpage
  if (pathname === '/' || pathname === '/frontpage') {
    return { surface_type: 'lohp' };
  }

  // Premium Hub pages: /certificates, /certificates/learn, /certificates/:categorySlug, /mastertrack, /professional-certificates
  if (pathname.startsWith('/certificates')) {
    const certCategoryMatch = pathname.match(/^\/certificates\/([^/]+)/);
    if (!certCategoryMatch) {
      return { surface_type: 'category', topical_anchor: 'Certificates Hub' };
    }

    const matchCategory = certCategoryMatch?.[1];
    if (matchCategory === 'learn') {
      return { surface_type: 'category', topical_anchor: 'University Certificates Hub' };
    }

    return { surface_type: 'category', ...(matchCategory ? { topical_anchor: slugToDisplayName(matchCategory) } : {}) };
  }
  if (pathname.startsWith('/mastertrack')) {
    return { surface_type: 'category', topical_anchor: 'Mastertrack Certificates Hub' };
  }
  if (pathname.startsWith('/professional-certificates')) {
    return { surface_type: 'category', topical_anchor: 'Professional Certificates Hub' };
  }

  return null;
};

const useNavAiSparkleSurfaceInfo = (): AiCoachAdditionalContextInput[] => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const info = getSurfaceInfoFromPath(pathname);
    if (!info) return [];

    const entries: AiCoachAdditionalContextInput[] = [
      { contextKey: 'additionalContext.surface_context', contextValue: JSON.stringify(info) },
    ];

    return entries;
  }, [pathname]);
};

export default useNavAiSparkleSurfaceInfo;
